import { Howl } from 'howler';
import { SettingsManager } from './settingsManager';

export interface Sound {
    name: string;
    path: string;
}

export class AudioManager {
    private static instance: AudioManager;
    private sounds: Map<string, Howl>;
    private music: Map<string, Howl>;
    private currentMusic: Howl | null = null;
    private currentMusicName: string | null = null;
    public isInitialized: boolean = false;
    private enabled: boolean = true;
    private musicEnabled: boolean = true;
    private volume: number = 1.0;
    private musicVolume: number = 0.25; // Reduced from 0.5 to 0.25 (half volume)
    private settingsManager: SettingsManager;

    // Public sounds array that can be used to play sounds
    public static readonly SOUNDS = {
        WelcomeZoom: {
            name: 'WelcomeZoom',
            path: 'sounds/woosh.mp3'
        },
        Click: {
            name: 'Click',
            path: 'sounds/click.mp3'
        },
        Running1: {
            name: 'Running1',
            path: 'sounds/running1.mp3'
        },
        Running2: {
            name: 'Running2',
            path: 'sounds/running2.mp3'
        },
        Running3: {
            name: 'Running3',
            path: 'sounds/running3.mp3'
        },
        SwordSlash1: {
            name: 'SwordSlash1',
            path: 'sounds/swordSlash1.mp3'
        },
        SwordSlash2: {
            name: 'SwordSlash2',
            path: 'sounds/swordSlash2.mp3'
        },
        HealthPickup: {
            name: 'HealthPickup',
            path: 'sounds/healthPickup.mp3'
        },
        SkeletonEngage1: {
            name: 'SkeletonEngage1',
            path: 'sounds/skeletonEngage1.mp3'
        },
        SkeletonEngage2: {
            name: 'SkeletonEngage2',
            path: 'sounds/skeletonEngage2.mp3'
        },
        SkeletonEngage3: {
            name: 'SkeletonEngage3',
            path: 'sounds/skeletonEngage3.mp3'
        },
        SkeletonDied1: {
            name: 'SkeletonDied1',
            path: 'sounds/skeletonDied1.mp3'
        },
        PlayerSwordSlash: {
            name: 'PlayerSwordSlash',
            path: 'sounds/swordSlashPlayer.m4a'
        },
        PlayerSwordSlashHit: {
            name: 'PlayerSwordSlashHit',
            path: 'sounds/swordSlashPlayerHit.mp3'
        }
    } as const;

    // Background music tracks
    public static readonly MUSIC = {
        Menu: {
            name: 'Menu',
            path: 'sounds/background/menu.m4a'
        },
        Game: {
            name: 'Game',
            path: 'sounds/background/game.m4a'
        }
    } as const;

    private constructor() {
        this.sounds = new Map();
        this.music = new Map();
        this.settingsManager = SettingsManager.getInstance();
        
        // Load audio settings
        const settings = this.settingsManager.getSettings();
        this.enabled = settings.audio.soundEnabled;
        this.volume = settings.audio.soundVolume;
        this.musicEnabled = settings.audio.musicEnabled;
        this.musicVolume = settings.audio.musicVolume * 0.25; // Apply our internal scaling
    }

    public static getInstance(): AudioManager {
        if (!AudioManager.instance) {
            AudioManager.instance = new AudioManager();
        }
        return AudioManager.instance;
    }

    public async init(): Promise<void> {
        if (this.isInitialized) return;

        // Load all sounds
        await this.loadSounds();
        
        // Load all music
        await this.loadMusic();

        this.isInitialized = true;
    }

    private loadSounds(): Promise<void> {
        return new Promise((resolve) => {
            const totalSounds = Object.keys(AudioManager.SOUNDS).length;
            let loadedSounds = 0;

            // Load each sound defined in SOUNDS
            Object.values(AudioManager.SOUNDS).forEach(sound => {
                const howl = new Howl({
                    src: [sound.path],
                    loop: false,
                    volume: this.volume,
                    onload: () => {
                        this.sounds.set(sound.name, howl);
                        loadedSounds++;
                        if (loadedSounds === totalSounds) {
                            resolve();
                        }
                    },
                    onloaderror: (id, error) => {
                        console.error(`Failed to load sound ${sound.name}:`, error);
                        loadedSounds++;
                        if (loadedSounds === totalSounds) {
                            resolve();
                        }
                    }
                });
            });
        });
    }

    private loadMusic(): Promise<void> {
        return new Promise((resolve) => {
            const totalMusic = Object.keys(AudioManager.MUSIC).length;
            if (totalMusic === 0) {
                resolve();
                return;
            }
            
            let loadedMusic = 0;

            // Load each music track defined in MUSIC
            Object.values(AudioManager.MUSIC).forEach(music => {
                const howl = new Howl({
                    src: [music.path],
                    loop: true,
                    volume: this.musicVolume,
                    onload: () => {
                        this.music.set(music.name, howl);
                        loadedMusic++;
                        if (loadedMusic === totalMusic) {
                            resolve();
                        }
                    },
                    onloaderror: (id, error) => {
                        console.error(`Failed to load music ${music.name}:`, error);
                        loadedMusic++;
                        if (loadedMusic === totalMusic) {
                            resolve();
                        }
                    }
                });
            });
        });
    }

    public playSound(sound: Sound, volume: number = 1.0): void {
        if (!this.isInitialized || !this.enabled) {
            return;
        }

        const howl = this.sounds.get(sound.name);
        howl.volume(volume);
        if (!howl) {
            console.warn(`Sound ${sound.name} not found`);
            return;
        }

        howl.play();
        //console.log(`Playing sound: ${sound.name}`);
    }

    public playMusic(music: Sound, fadeInDuration: number = 1000): void {
        if (!this.isInitialized || !this.musicEnabled) {
            return;
        }

        // Stop any currently playing music
        this.stopMusic();

        const howl = this.music.get(music.name);
        if (!howl) {
            console.warn(`Music ${music.name} not found`);
            return;
        }

        // Set current music
        this.currentMusic = howl;
        this.currentMusicName = music.name;
        
        // Start with volume at 0 if using fade-in
        if (fadeInDuration > 0) {
            howl.volume(0);
            howl.play();
            // Fade in the music
            this.fadeInMusic(fadeInDuration);
        } else {
            // Play at normal volume
            howl.volume(this.musicVolume);
            howl.play();
        }
        
        //console.log(`Playing music: ${music.name}`);
    }

    public stopMusic(): void {
        if (this.currentMusic) {
            this.currentMusic.stop();
            this.currentMusic = null;
            this.currentMusicName = null;
        }
    }

    public fadeOutMusic(duration: number = 1000): void {
        if (this.currentMusic) {
            const music = this.currentMusic;
            const currentVolume = music.volume();
            
            // Create a smooth fade out
            const startTime = Date.now();
            const fadeInterval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const factor = Math.max(0, 1 - elapsed / duration);
                
                music.volume(currentVolume * factor);
                
                if (elapsed >= duration) {
                    music.stop();
                    clearInterval(fadeInterval);
                    
                    if (this.currentMusic === music) {
                        this.currentMusic = null;
                        this.currentMusicName = null;
                    }
                }
            }, 16); // ~60fps
        }
    }

    public fadeInMusic(duration: number = 1000): void {
        if (this.currentMusic) {
            const music = this.currentMusic;
            const targetVolume = this.musicVolume;
            
            // Create a smooth fade in
            const startTime = Date.now();
            const fadeInterval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const factor = Math.min(1, elapsed / duration);
                
                music.volume(targetVolume * factor);
                
                if (elapsed >= duration) {
                    music.volume(targetVolume);
                    clearInterval(fadeInterval);
                }
            }, 16); // ~60fps
        }
    }

    public setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        if (!enabled) {
            // Stop all sounds when disabled
            this.sounds.forEach(howl => {
                howl.stop();
            });
            
            // Stop music
            this.stopMusic();
        }
        
        // Save setting
        this.settingsManager.updateAudioSettings(enabled, this.volume, this.musicEnabled, this.musicVolume / 0.25);
    }

    public setVolume(volume: number): void {
        // Clamp volume between 0 and 1
        this.volume = Math.max(0, Math.min(1, volume));
        
        // Update volume for all sounds
        this.sounds.forEach(howl => {
            howl.volume(this.volume);
        });
        
        // Save setting
        this.settingsManager.updateAudioSettings(this.enabled, this.volume, this.musicEnabled, this.musicVolume / 0.25);
    }
    
    public setMusicVolume(volume: number): void {
        // Clamp volume between 0 and 1
        this.musicVolume = Math.max(0, Math.min(1, volume)) * 0.25; // Apply scaling
        
        // Update current music volume if playing
        if (this.currentMusic) {
            this.currentMusic.volume(this.musicVolume);
        }
        
        // Save setting (store without scaling for consistency)
        this.settingsManager.updateAudioSettings(this.enabled, this.volume, this.musicEnabled, volume);
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    public getVolume(): number {
        return this.volume;
    }
    
    public getMusicVolume(): number {
        return this.musicVolume;
    }

    public cleanup(): void {
        if (!this.isInitialized) return;

        // Stop and unload all sounds
        this.sounds.forEach(howl => {
            howl.stop();
            howl.unload();
        });
        
        // Stop and unload all music
        this.music.forEach(howl => {
            howl.stop();
            howl.unload();
        });

        // Clear sound and music maps
        this.sounds.clear();
        this.music.clear();
        this.currentMusic = null;
        this.currentMusicName = null;

        this.isInitialized = false;
    }

    public async playMenuMusic(): Promise<void> {
        // Ensure audio manager is initialized before playing
        if (!this.isInitialized) {
            await this.init();
        }
        
        // Now play the menu music (safer after initialization)
        this.playMusic(AudioManager.MUSIC.Menu);
    }

    public setMusicEnabled(enabled: boolean, context: 'menu' | 'game' = 'game'): void {
        this.musicEnabled = enabled;
        if (!enabled) {
            // Stop music when disabled
            this.stopMusic();
        } else if (this.isInitialized) {
            // Restart music if it was previously stopped
            if (this.currentMusicName) {
                // Try to play the last music that was playing
                const lastMusic = Object.values(AudioManager.MUSIC).find(m => m.name === this.currentMusicName);
                if (lastMusic) {
                    this.playMusic(lastMusic);
                } else if (context === 'menu') {
                    // Default to menu music if in menu context
                    this.playMusic(AudioManager.MUSIC.Menu);
                } else {
                    // Default to game music if in game context
                    this.playMusic(AudioManager.MUSIC.Game);
                }
            } else if (context === 'menu') {
                // No music was playing before, play menu music if in menu context
                this.playMusic(AudioManager.MUSIC.Menu);
            } else {
                // No music was playing before, play game music by default (in game context)
                this.playMusic(AudioManager.MUSIC.Game);
            }
        }
        
        // Save setting
        this.settingsManager.updateAudioSettings(this.enabled, this.volume, enabled, this.musicVolume / 0.25);
    }

    public isMusicEnabled(): boolean {
        return this.musicEnabled;
    }
} 